import { useUser } from "@/api/hooks/user";
import { useTimeHandler } from "@/hooks";
import { AmbientAiChatListener } from "@/overlay/components/AiChatAmbient/AiChatAmbient";
import { useAppDispatch } from "@/reducer";
import { getFeatureValue } from "@/types/featureSet";
import {
  SHORTCUTS,
  SHORTCUT_DEFS,
  SHORTCUT_TRIGGERED_EVENT,
  type ShortcutKey,
  isShortcutTriggered,
} from "@/utility/key-shortcuts/shortcuts";
import { Duration } from "luxon";
import { useCallback, useEffect, useRef } from "react";
import { ActionCreators } from "redux-undo";

export type Shortcut = {
  default: readonly string[];
  mac?: readonly string[];
};

type WorkspaceKeyShortcutProviderProps = {
  children: React.ReactNode;
};

export function WorkspaceKeyShortcutProvider(props: WorkspaceKeyShortcutProviderProps) {
  const dispatch = useAppDispatch();
  const { handleTimeChange } = useTimeHandler();
  const { features } = useUser();
  const hasShortcut = !!getFeatureValue(features, "ui.account.shortcuts");
  const hasAIChat = !!getFeatureValue(features, "ui.ui.chatbot.enabled");
  const activeShortcutsRef = useRef<Partial<Record<ShortcutKey, boolean>>>({});

  if (!hasShortcut) {
    return <>{props.children}</>;
  }

  const handleNavigationTimeChange = useCallback(
    (duration: Duration) => {
      handleTimeChange(duration);
    },
    [handleTimeChange],
  );

  type ShortcutHandlers = {
    onKeyDown?: () => void;
    onKeyUp?: () => void;
    hold?: boolean;
  };

  const shortcutHandlers: Record<ShortcutKey, ShortcutHandlers> = {
    [SHORTCUTS.PLUS_5_MINUTES]: {
      onKeyDown: () => handleNavigationTimeChange(Duration.fromObject({ minutes: 5 })),
    },
    [SHORTCUTS.MINUS_5_MINUTES]: {
      onKeyDown: () => handleNavigationTimeChange(Duration.fromObject({ minutes: -5 })),
    },
    [SHORTCUTS.PLUS_ONE_HOUR]: {
      onKeyDown: () => handleNavigationTimeChange(Duration.fromObject({ hours: 1 })),
    },
    [SHORTCUTS.MINUS_ONE_HOUR]: {
      onKeyDown: () => handleNavigationTimeChange(Duration.fromObject({ hours: -1 })),
    },
    [SHORTCUTS.PLUS_ONE_DAY]: {
      onKeyDown: () => handleNavigationTimeChange(Duration.fromObject({ days: 1 })),
    },
    [SHORTCUTS.MINUS_ONE_DAY]: {
      onKeyDown: () => handleNavigationTimeChange(Duration.fromObject({ days: -1 })),
    },
    [SHORTCUTS.UNDO]: {
      onKeyDown: () => dispatch(ActionCreators.undo()),
    },
    [SHORTCUTS.REDO]: {
      onKeyDown: () => dispatch(ActionCreators.redo()),
    },
    // TODO: Enable once toast messages work fine AND check hasAIChat
    // [SHORTCUTS.AMBIENT_VOICE]: {
    //   hold: true,
    //   onKeyDown: () => document.dispatchEvent(new CustomEvent("ambientVoiceStart")),
    //   onKeyUp: () => document.dispatchEvent(new CustomEvent("ambientVoiceStop")),
    // },
  };

  useEffect(() => {
    const shortcutDefs = SHORTCUT_DEFS;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target && "editor" in event.target) {
        return;
      }

      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (event.target instanceof HTMLElement && event.target.classList.contains("editable-textbox")) {
        return;
      }

      if (event.target instanceof HTMLElement && event.target.contentEditable === "true") {
        return;
      }

      for (const [shortcutKey, shortcut] of Object.entries(shortcutDefs)) {
        const key = shortcutKey as ShortcutKey;
        const handler = shortcutHandlers[key];
        const shouldIgnoreRepeat = handler?.hold && event.repeat;
        if (shouldIgnoreRepeat) {
          continue;
        }
        if (isShortcutTriggered(event, shortcut)) {
          event.preventDefault();
          event.stopPropagation();

          activeShortcutsRef.current[key] = true;

          document.dispatchEvent(
            new CustomEvent(SHORTCUT_TRIGGERED_EVENT, {
              detail: { shortcut: key },
            }),
          );

          handler?.onKeyDown?.();
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      for (const [shortcutKey, shortcut] of Object.entries(shortcutDefs)) {
        const key = shortcutKey as ShortcutKey;
        const handler = shortcutHandlers[key];
        if (!handler?.onKeyUp) {
          continue;
        }
        const wasActive = activeShortcutsRef.current[key];
        const triggeredNow = isShortcutTriggered(event, shortcut);
        if (wasActive || triggeredNow) {
          handler.onKeyUp();
          activeShortcutsRef.current[key] = false;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [dispatch, handleNavigationTimeChange, shortcutHandlers]);

  return (
    <>
      <AmbientAiChatListener />
      {props.children}
    </>
  );
}
